import React, { useState } from 'react';

interface TicketInfo {
  registrationId: string;
  tierName: string;
  priceAtPurchase: number;
  status: 'Active' | 'Cancelled';
  referenceId: string;
}

interface TicketCardProps {
  ticket: TicketInfo | null;
  isCheckedIn?: boolean;
}

export function TicketCard({ ticket, isCheckedIn = false }: TicketCardProps) {
  const [showPerks, setShowPerks] = useState(false);

  if (!ticket) {
    return (
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>🎟️ My Ticket</h3>
        <p style={styles.emptyMessage}>No ticket found — please contact the organizer.</p>
      </div>
    );
  }

  const isCancelled = ticket.status === 'Cancelled';
  const isPatron = ticket.tierName === 'Patron';

  return (
    <div style={{ ...styles.card, ...(isCancelled ? styles.cardCancelled : {}) }}>
      <h3 style={styles.cardTitle}>🎟️ My Ticket</h3>

      <div style={styles.content}>
        <div style={styles.details}>
          <div style={styles.field}>
            <span style={styles.label}>Tier</span>
            <span style={styles.value}>
              {ticket.tierName}
              <button
                onClick={() => setShowPerks(!showPerks)}
                style={styles.patronBadgeBtn}
                aria-label={`View ${ticket.tierName} tier perks`}
                aria-expanded={showPerks}
              >
                <span style={styles.patronBadgeText}>{isPatron ? '👑' : '🎟️'} Perks</span>
                <span style={styles.patronInfoIcon}>{showPerks ? '▾' : '▸'}</span>
              </button>
            </span>
          </div>
          <div style={styles.field}>
            <span style={styles.label}>Status</span>
            <span style={isCancelled ? styles.statusCancelled : styles.statusActive}>
              {ticket.status}
            </span>
          </div>
        </div>

        <div style={isCheckedIn ? styles.referenceBadgeCheckedIn : styles.referenceBadge}>
          <span style={isCheckedIn ? styles.referenceLabelCheckedIn : styles.referenceLabel}>Reference ID</span>
          <span style={{ ...styles.referenceId, ...(isCancelled ? styles.referenceIdCancelled : {}), ...(isCheckedIn ? styles.referenceIdCheckedIn : {}) }}>
            {ticket.referenceId}
          </span>
        </div>
      </div>

      {/* Expandable Perks Panel */}
      {showPerks && (
        <div style={styles.perksPanel}>
          <div style={styles.perksHeader}>
            <span style={styles.perksTitle}>{isPatron ? '👑' : '🎟️'} {ticket.tierName} Perks</span>
            <button
              onClick={() => setShowPerks(false)}
              style={styles.perksClose}
              aria-label="Close perks panel"
            >✕</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.25rem 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.85rem' }}>
              <span style={{ fontSize: '1.1rem' }}>🍽️</span>
              <span>3-course meal</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.85rem' }}>
              <span style={{ fontSize: '1.1rem' }}>🍺</span>
              <span>One (1) stout or beer</span>
            </div>
            {isPatron && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.85rem' }}>
                  <span style={{ fontSize: '1.1rem' }}>🍷</span>
                  <span>Two (2) glasses of wine OR liquor</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.85rem' }}>
                  <span style={{ fontSize: '1.1rem' }}>🎁</span>
                  <span>A surprise gift</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.85rem' }}>
                  <span style={{ fontSize: '1.1rem' }}>👑</span>
                  <span>Exclusive Patron identifier</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    padding: '1.25rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  cardCancelled: {
    opacity: 0.7,
    borderColor: '#d32f2f',
    borderWidth: '2px',
  },
  cardTitle: {
    color: '#0f2d1f',
    fontSize: '1.1rem',
    margin: '0 0 1rem 0',
    borderBottom: '2px solid #c9a84c',
    paddingBottom: '0.5rem',
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap' as const,
  },
  details: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
  },
  field: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  label: {
    fontSize: '0.8rem',
    color: '#666',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    fontWeight: 600,
  },
  value: {
    fontSize: '0.95rem',
    color: '#333',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
  },
  statusActive: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#1a6b3c',
    backgroundColor: '#e8f5e9',
    padding: '0.15rem 0.5rem',
    borderRadius: '12px',
  },
  statusCancelled: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.15rem 0.5rem',
    borderRadius: '12px',
  },
  referenceBadge: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '0.75rem 1rem',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    border: '1px dashed #c9a84c',
    flex: '1 1 auto',
    minWidth: '120px',
  },
  referenceBadgeCheckedIn: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '0.75rem 1rem',
    backgroundColor: '#16a34a',
    borderRadius: '8px',
    border: '1px solid #16a34a',
    flex: '1 1 auto',
    minWidth: '120px',
  },
  referenceLabel: {
    fontSize: '0.7rem',
    color: '#666',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '0.25rem',
  },
  referenceLabelCheckedIn: {
    fontSize: '0.7rem',
    color: '#ffffff',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '0.25rem',
  },
  referenceId: {
    fontSize: '1.2rem',
    fontWeight: 700,
    color: '#1a6b3c',
    letterSpacing: '1px',
    fontFamily: 'monospace',
  },
  referenceIdCancelled: {
    color: '#999',
    textDecoration: 'line-through',
  },
  referenceIdCheckedIn: {
    color: '#ffffff',
  },
  emptyMessage: {
    color: '#666',
    fontSize: '0.9rem',
    margin: 0,
    fontStyle: 'italic',
  },
  // Patron badge as a tappable button
  patronBadgeBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '0.15rem 0.6rem',
    backgroundColor: '#e6e0f3',
    color: '#4a3080',
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: '12px',
    marginLeft: '0.5rem',
    letterSpacing: '0.3px',
    border: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    minHeight: '28px',
    minWidth: '44px',
    touchAction: 'manipulation' as const,
  },
  patronBadgeText: {
    lineHeight: 1,
  },
  patronInfoIcon: {
    fontSize: '0.7rem',
    opacity: 0.8,
  },
  // Perks expandable panel
  perksPanel: {
    marginTop: '1rem',
    padding: '0.75rem 1rem',
    backgroundColor: '#f9f5ff',
    border: '1px solid #e0d4f5',
    borderRadius: '8px',
    animation: 'fadeIn 0.2s ease-in',
  },
  perksHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  perksTitle: {
    fontSize: '0.85rem',
    fontWeight: 700,
    color: '#4a3080',
  },
  perksClose: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    color: '#666',
    padding: '4px 8px',
    minWidth: '44px',
    minHeight: '44px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  perksBody: {
    fontSize: '0.85rem',
    color: '#444',
    lineHeight: 1.5,
    margin: 0,
    whiteSpace: 'pre-line' as const,
  },
  perksLoading: {
    fontSize: '0.8rem',
    color: '#999',
    margin: 0,
  },
};
